import { REASONING_EFFORT_LABELS, REASONING_EFFORTS } from "@/constants/reasoningEffort";
import type { ReasoningConfig, ReasoningEffort } from "@/types";

export interface ModelReasoningConfigFieldsProps {
  idPrefix: string;
  /** Mirrors the "Reasoning Model" flag; efforts only make sense for reasoning models. */
  enabled: boolean;
  value: ReasoningConfig | null;
  onChange: (value: ReasoningConfig | null) => void;
}

/** Curates which reasoning efforts a model accepts, plus the informational default (MHS-669). */
export function ModelReasoningConfigFields({
  idPrefix,
  enabled,
  value,
  onChange,
}: Readonly<ModelReasoningConfigFieldsProps>) {
  if (!enabled) {
    return (
      <p className="text-xs text-content-quaternary">Enable "Reasoning Model" to curate efforts for this model.</p>
    );
  }

  const supported = value?.supportedEfforts ?? [];
  const defaultEffort = value?.defaultEffort ?? null;

  const toggle = (effort: ReasoningEffort, checked: boolean) => {
    const next = REASONING_EFFORTS.filter((e) => (e === effort ? checked : supported.includes(e)));
    if (next.length === 0) {
      onChange(null);
      return;
    }
    onChange({
      supportedEfforts: next,
      defaultEffort: defaultEffort && next.includes(defaultEffort) ? defaultEffort : null,
    });
  };

  return (
    <fieldset className="min-w-0 space-y-3">
      <legend className="text-sm font-medium text-content-secondary">Reasoning effort</legend>
      <p className="text-xs text-content-quaternary">
        Efforts users may pick for this model. Unlisted values are dropped before the request is sent.
      </p>
      <div className="grid grid-cols-4 gap-2">
        {REASONING_EFFORTS.map((effort) => (
          <label
            key={effort}
            htmlFor={`${idPrefix}-effort-${effort}`}
            className="flex items-center gap-2 text-sm text-content-primary"
          >
            <input
              id={`${idPrefix}-effort-${effort}`}
              type="checkbox"
              checked={supported.includes(effort)}
              onChange={(e) => toggle(effort, e.target.checked)}
              className="w-4 h-4 rounded-sm border-border-secondary text-brand-primary focus:ring-brand-primary"
            />
            {REASONING_EFFORT_LABELS[effort]}
          </label>
        ))}
      </div>
      {supported.length > 0 && (
        <div>
          <label htmlFor={`${idPrefix}-default-effort`} className="block text-xs text-content-tertiary mb-1">
            Default effort (label only)
          </label>
          <select
            id={`${idPrefix}-default-effort`}
            value={defaultEffort ?? ""}
            onChange={(e) =>
              onChange({
                supportedEfforts: supported,
                defaultEffort: (e.target.value || null) as ReasoningEffort | null,
              })
            }
            className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
          >
            <option value="">Not set</option>
            {supported.map((effort) => (
              <option key={effort} value={effort}>
                {REASONING_EFFORT_LABELS[effort]}
              </option>
            ))}
          </select>
        </div>
      )}
    </fieldset>
  );
}
